import { z } from 'zod';

/**
 * Los esquemas del aporte y el retiro de socio.
 *
 * Validan la forma en el formulario. La autoridad sigue estando en Postgres, y por eso los mensajes
 * son los códigos del contrato: la pantalla traduce un solo diccionario, venga el rechazo del
 * navegador o del servidor. Es el mismo criterio de `esquemas/jornada.ts`.
 *
 * LAS DOS CUENTAS SON LAS QUE LA BASE ADMITE, ni una más. `app.registrar_retiro_socio` acepta
 * `banco_principal` y `caja_ahorro` desde la tarea 8 de la fase 10, y `app.registrar_aporte_socio`
 * las mismas dos. Una tercera acá se rechazaría en el servidor con `E_ASIENTO_NO_PERMITIDO`, que es
 * un mensaje sobre la dirección del asiento y no sobre un campo del formulario.
 *
 * `confirmaCompromisos` NO TIENE `default(false)` A PROPÓSITO, y el bloque del plan se lo ponía. Un
 * valor por omisión invita a omitir el campo, y omitirlo en un retiro que compromete la operación es
 * exactamente el caso que la confirmación consciente existe para impedir. Quien arma el objeto tiene
 * que escribir el booleano, y entonces tiene que haber pensado en él.
 */

const fechaContable = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'E_SOLICITUD_INVALIDA');

const pesos = z.number().int('E_MONTO_NEGATIVO').positive('E_MONTO_NEGATIVO');

const cuentaDeCapital = z.enum(['banco_principal', 'caja_ahorro'], {
  message: 'E_ASIENTO_NO_PERMITIDO',
});

// La nota es opcional de verdad: `public.movimiento_capital.nota` admite nulo. Una cadena vacía y un
// nulo no son lo mismo para la base, así que el vacío se normaliza a nulo acá y no en la pantalla.
const nota = z
  .string()
  .trim()
  .max(200, 'E_SOLICITUD_INVALIDA')
  .transform((v) => (v === '' ? null : v))
  .nullable();

export const esquemaAporteSocio = z.object({
  socioId: z.string().uuid('E_SOCIO_NO_EXISTE'),
  montoCop: pesos,
  cuentaDestino: cuentaDeCapital,
  fecha: fechaContable,
  nota,
});
export type EntradaAporteSocio = z.infer<typeof esquemaAporteSocio>;

export const esquemaRetiroSocio = z.object({
  socioId: z.string().uuid('E_SOCIO_NO_EXISTE'),
  montoCop: pesos,
  cuentaOrigen: cuentaDeCapital,
  fecha: fechaContable,
  nota,
  confirmaCompromisos: z.boolean(),
});
export type EntradaRetiroSocio = z.infer<typeof esquemaRetiroSocio>;

/**
 * Lo que devuelve `app.evaluar_retiro_socio`, que es un `jsonb` y por lo tanto no tiene forma
 * garantizada al llegar. Se parsea antes de mirarlo: la pantalla decide con `requiereConfirmacion`
 * si muestra la confirmación consciente, y una llave ausente que se lee como `undefined` es
 * falsa, así que un cambio en el servidor dejaría pasar sin confirmar justo el retiro que hay que
 * confirmar.
 */
export const esquemaEvaluacionRetiro = z.object({
  cuenta_origen: z.string(),
  saldo_cuenta_origen_cop: z.union([z.string(), z.number()]).transform(Number),
  disponible_cop: z.union([z.string(), z.number()]).transform(Number),
  compromisos_cop: z.union([z.string(), z.number()]).transform(Number),
  monto_cop: z.union([z.string(), z.number()]).transform(Number),
  disponible_despues_cop: z.union([z.string(), z.number()]).transform(Number),
  faltante_cop: z.union([z.string(), z.number()]).transform(Number),
  saldo_insuficiente: z.boolean(),
  requiere_confirmacion: z.boolean(),
  motivo: z.string().nullable(),
});

export type EvaluacionRetiro = {
  cuentaOrigen: string;
  saldoCuentaOrigenCop: number;
  disponibleCop: number;
  compromisosCop: number;
  montoCop: number;
  disponibleDespuesCop: number;
  faltanteCop: number;
  saldoInsuficiente: boolean;
  requiereConfirmacion: boolean;
  motivo: string | null;
};

export function parseEvaluacionRetiro(json: unknown): EvaluacionRetiro {
  const e = esquemaEvaluacionRetiro.parse(json);
  return {
    cuentaOrigen: e.cuenta_origen,
    saldoCuentaOrigenCop: e.saldo_cuenta_origen_cop,
    disponibleCop: e.disponible_cop,
    compromisosCop: e.compromisos_cop,
    montoCop: e.monto_cop,
    disponibleDespuesCop: e.disponible_despues_cop,
    faltanteCop: e.faltante_cop,
    saldoInsuficiente: e.saldo_insuficiente,
    requiereConfirmacion: e.requiere_confirmacion,
    motivo: e.motivo,
  };
}
